import React from 'react'
import AntdSwitch from 'antd/es/switch'

import { useDesignMode } from 'src/design-mode/useDesignMode'

import type { SwitchProps } from './antd-props'
import { Switch as ShadcnSeamSwitch } from './Switch.shadcn'

/**
 * Compat seam leaf for antd's `antd/es/switch` — one of the three plain form
 * controls put behind the design toggle in the choice batch (Checkbox, Radio,
 * Switch). Read `./Checkbox.tsx` first; it carries the batch's reasoning.
 *
 * A LEAF, not the `src/antd` barrel (PRO-130).
 *
 * With the toggle off — the shipped default, and every page load of anyone
 * who has not picked "New design" under Theme —
 * this renders antd's own Switch with the props and the ref it was given:
 * same element, same class names, same DOM.
 * `./__tests__/switch-seam-parity.test.tsx` is the test to keep green.
 *
 * antd types the ref as `HTMLElement`; both branches hand back the rendered
 * `<button>`, which is what the antd path already gave.
 */
const SwitchDispatcher = React.forwardRef<HTMLElement, SwitchProps>(
  (props, ref) => {
    const designMode = useDesignMode()

    if (designMode === 'shadcn')
      return (
        <ShadcnSeamSwitch
          {...props}
          ref={ref as React.Ref<HTMLButtonElement>}
        />
      )

    return <AntdSwitch {...props} ref={ref} />
  }
)

SwitchDispatcher.displayName = 'Switch'

export const Switch = SwitchDispatcher
